import { Flex } from '@mantine/core';
import classNames from 'classnames';
import { autorun, runInAction, set, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Account,
  DisplayCampaign,
  IChannelBudgets,
  IRecommendedSplit,
  OmniCampaign,
  Targeting,
  TTDCampaign,
} from '@feathr/blackbox';
import { getMaxTargetValue, getMinDuration } from '@feathr/blackbox';
import type { TAlertType } from '@feathr/components';
import { Alert, CardV2 as Card, DatePicker, EmptyState, NumberInput } from '@feathr/components';
import { getTargetablesFromTargetings } from '@feathr/extender/components/Targetings/Targetings.utils';
import { useBlackboxVersion, useCampaignChannelVisibility } from '@feathr/extender/hooks';
import { useFlags, useStore, useUser } from '@feathr/extender/state';
import { cssVar, moment, momentToDate, TimeFormat } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';
import { TargetAudienceOverview, useAudienceBreakdown } from '@feathr/report_components';

import { getTargetSegments } from '../AdWizardAudienceStep';
import {
  getMetaLookalikeLabel,
  valueToSlider,
} from '../AdWizardAudienceStep/LookalikeAudienceTargeting/LookalikeAudienceTargeting.utils';
import {
  getMinBudget,
  getRecommendedImpressions,
  validateStepBudget,
} from './AdWizardBudgetStep.utils';
import ChannelBudgetSplit from './ChannelBudgetSplit';
import RecommendedBudget from './RecommendedBudget';

import * as wizardStyles from '../AdWizard.css';
import * as styles from './AdWizardBudgetStep.css';

interface IProps {
  account: Account;
  campaign: DisplayCampaign;
  targetings: ListResponse<Targeting>;
  /**
   * Backend per-channel/total minimum budgets, lifted to AdWizard so the publish
   * gate enforces the split and the value survives step navigation. RecommendedBudget
   * populates it via `setChannelMinimums`; null until the recommendation loads.
   *
   * Optional because single-channel wizards (e.g. FacebookWizard) reuse this step
   * without a channel split — they leave these unset.
   */
  channelMinimums?: IChannelBudgets | null;
  setChannelMinimums?: (budgets: IChannelBudgets) => void;
  /**
   * True when the recommendation was rejected for exceeding the account maximum.
   * Lifted to AdWizard so the publish/next gate can block on it. RecommendedBudget
   * sets it via `setBudgetExceedsAccountMax`.
   */
  budgetExceedsAccountMax?: boolean;
  setBudgetExceedsAccountMax?: (exceeds: boolean) => void;
  /**
   * True while a budget recommendation request is in flight. Lifted to AdWizard
   * so the Next/Publish gate can block during the re-fetch (the split is
   * recomputing). RecommendedBudget sets it via `setIsRecommendationLoading`.
   */
  isRecommendationLoading?: boolean;
  setIsRecommendationLoading?: (loading: boolean) => void;
  /**
   * Flags upward when the budget recommendation fails (generic error), so the
   * Next/Publish gate blocks — without minimums the budget can't be validated.
   * RecommendedBudget sets it.
   */
  setBudgetRecommendationFailed?: (failed: boolean) => void;
}

function AdWizardBudgetStep({
  account,
  campaign,
  targetings: targetingsList,
  channelMinimums = null,
  setChannelMinimums = (): void => {},
  budgetExceedsAccountMax = false,
  setBudgetExceedsAccountMax = (): void => {},
  isRecommendationLoading = false,
  setIsRecommendationLoading = (): void => {},
  setBudgetRecommendationFailed = (): void => {},
}: Readonly<IProps>): JSX.Element {
  const { Segments, Targetables } = useStore();
  const { noMaxBudget } = useFlags();
  const { showDisplay, showMeta } = useCampaignChannelVisibility(campaign);
  const user = useUser();
  const { t } = useTranslation();
  const { isOnProduction } = useBlackboxVersion();
  const originalBudgetRef = useRef<number | null>(null);
  const audienceData = useAudienceBreakdown({
    targetings: targetingsList,
    campaign,
    showMeta,
  });

  const sliderPosition = audienceData.lookalikeAudienceSize
    ? valueToSlider(audienceData.lookalikeAudienceSize)
    : undefined;
  const metaLookalikeLabel = getMetaLookalikeLabel(sliderPosition, t);

  const targetings = targetingsList.models;

  const { isFacebook, isMonetization, isDraft, isOmniCampaign, isOmniReachCampaign } = campaign;
  const isDeprecatedMetaCampaign = isFacebook;
  const segments = getTargetSegments(targetings, Segments);

  // Use getTargetablesFromTargetings to include ephemeral/newly saved targetables from local store
  // and get the loading state in a single call
  const { targetables, isPending: isLoadingTargetables } = getTargetablesFromTargetings(
    targetings.filter((t) => t.get('kind') !== 'segment'),
    Targetables,
  );
  /* Backend-recommended Display/Meta split, populated by RecommendedBudget. */
  const [recommendedSplit, setRecommendedSplit] = useState<IRecommendedSplit | null>(null);

  /* Backend-recommended per-channel dollar amounts, populated by RecommendedBudget. */
  const [recommendedAmounts, setRecommendedAmounts] = useState<{
    display: number;
    meta: number;
  } | null>(null);

  /*
   * The recommendation-loading block is intentionally omitted here: this call
   * drives the inline budget-input error, and we don't want the transient
   * "calculating" copy under the field. The Next/Publish gate (AdWizard's own
   * validateStepBudget call) passes `isRecommendationLoading` so the buttons
   * still block while the split recomputes.
   */
  const validationErrors = validateStepBudget({
    campaign,
    account,
    user,
    noMaxBudget,
    isOnProduction,
    channelMinimums,
    budgetExceedsAccountMax,
  });

  const dateStart = moment.utc(campaign.get('date_start'), TimeFormat.isoDateTime);
  const dateEnd = moment.utc(campaign.get('date_end'), TimeFormat.isoDateTime);
  const canEditBudget = !!campaign.get('date_start') && !!campaign.get('date_end');
  const now = moment.utc();
  const [recBudget, setRecBudget] = useState<number | null>(null);
  // Prefer the backend minimum once loaded (bumped for whole-number split
  // feasibility on dual-channel); otherwise fall back to the frontend estimate.
  const minBudget = getMinBudget(campaign, channelMinimums);
  const accountMaxBudget = getMaxTargetValue(account.campaignsMaxBudget, isMonetization);

  const isComplete = dateEnd.isBefore(now);
  const isStarted = dateStart.isBefore(now);

  const target = getRecommendedImpressions(campaign, segments, targetables);
  const formattedTarget = numeral(target).format('0,0');
  // Default the recommended budget to the min budget if it's less than the min budget
  const recommendationRaw = Math.max(recBudget ?? 0, minBudget);
  const formattedBudget = numeral(recommendationRaw).format('$0,0.00');
  const minBudgetFormatted = numeral(minBudget).format('$0,0');

  const targetingsWithData = useMemo(() => {
    if (isLoadingTargetables) {
      return [];
    }

    const ephemeralTargetables = targetings
      .map((targeting: Targeting) => {
        const targetingAttributes = toJS(targeting).attributes;

        const matchingSegment = segments.find(
          (segment) => segment.id === targeting.get('target_data'),
        );

        const matchingTargetable = targetables.find(
          (targetable) => targetable.id === targeting.get('target_data'),
        );

        const data = {
          ...targetingAttributes,
          target_data: toJS(matchingSegment?.attributes) || toJS(matchingTargetable?.attributes),
        };

        return data;
      })
      .filter((targeting) => targeting.target_data !== undefined);
    return ephemeralTargetables;
  }, [targetings, targetables, segments, isLoadingTargetables]);

  useEffect(() => {
    return autorun(() => {
      const exposureSettings = campaign.get('exposure_settings');

      if (originalBudgetRef.current === null) {
        originalBudgetRef.current = exposureSettings?.target_value ?? 0;
      }

      if (!isMonetization) {
        /*
         * Only auto-fill the budget once the backend recommendation has loaded.
         * Before that we leave the campaign's own target_value alone rather than
         * clobbering it with 0 — the frontend no longer estimates a budget.
         */
        if (recBudget !== null && !exposureSettings.custom_target && isDraft) {
          // Only mark dirty if the value actually changes
          if (exposureSettings.target_value !== recommendationRaw) {
            runInAction((): void => {
              set(exposureSettings, { target_value: recommendationRaw });
              campaign.setAttributeDirty('exposure_settings');
            });
          }
        }
      }
    });
  }, [canEditBudget, recommendationRaw, isDraft, recBudget]);

  function handleTargetValueChange(newValue?: number): void {
    const exposureSettings = campaign.get('exposure_settings');
    if (exposureSettings) {
      runInAction((): void => {
        const budget = newValue ?? 0;
        set(exposureSettings, {
          target_value: budget,
          /*
           * Once the user sets a custom target, we no longer modify the values in the
           * budget input based on date selection.
           */
          custom_target: true,
          recommended_target_percent: budget / recommendationRaw,
        });
        if (budget !== originalBudgetRef.current) {
          campaign.setAttributeDirty('exposure_settings');
        } else {
          campaign.setAttributeClean('exposure_settings');
        }
      });
    }
  }

  function handleSetRecommendedBudget(): void {
    handleTargetValueChange(recommendationRaw);
  }

  function handleMonetizationValueChange(newValue?: number): void {
    campaign.set({ monetization_value: newValue ?? 0 });
  }

  let description: ReactNode;
  if (isMonetization) {
    description = t(
      'Set the target impressions for your campaign. Feathr tries to bid enough each day to meet your campaign impression target by the end of the campaign, but sometimes this is not possible due to limited audience availability.',
    );
  } else {
    description = t(
      'Feathr will suggest a budget based on the duration, audience size, and campaign type. Feathr aims to meet your campaign budget by the end, but limited audience availability may prevent this.',
    );
  }

  const timezone = user.get('timezone');

  // Time stamps
  const startTimeStamp = campaign.get('date_start');
  const endTimeStamp = campaign.get('date_end');

  // Moment objects
  const startMoment = moment.utc(startTimeStamp).tz(timezone);

  function handleOnChangeSendStart(newTimestamp?: string): void {
    campaign.set({
      date_start: newTimestamp,
    });
  }

  function handleOnChangeDateEnd(newTimestamp?: string): void {
    campaign.set({
      date_end: newTimestamp,
    });
  }

  const overspendText = isMonetization
    ? t(
        'Due to standard bidding dynamics, campaigns may under or overreach their impression target by ~5%',
      )
    : t('Due to standard bidding dynamics, campaigns may overspend by ~5%');

  /*
   * Only show the recommended target for monetization campaigns.
   * Do not show it if it's 0 (generally due to audience selection)
   */
  const targetRecommendation =
    isMonetization && target > 0
      ? t('Recommended target: {{target}}', { target: formattedTarget })
      : undefined;

  const alertMap = {
    ['info']: {
      title: t('You can get the most impact by using the recommended budget.'),
      description: t(
        'This campaign will perform most effectively with a budget of {{formattedBudget}}.',
        { formattedBudget },
      ),
      type: 'info' as const,
      name: 'alert_recommended_budget',
    },
    ['danger']: {
      title: t('This campaign cannot be published with the set budget.'),
      description: t('The minimum budget required to publish this campaign is {{min}}.', {
        min: minBudgetFormatted,
      }),
      type: 'danger' as const,
      name: 'alert_min_budget',
    },
  };

  const noDatesTarget = isMonetization ? t('impressions and sponsor package value') : t('budget');
  const targetValue = campaign.get('exposure_settings').target_value ?? 0;

  /*
   * The channel split can only be configured when the budget can support one.
   * We replace it with an empty state ("select a valid budget") when:
   * 1. the campaign's minimum exceeds the account's maximum (unfundable — known
   *    from the loaded minimums or a rejected recommendation), or
   * 2. the budget is strictly below the total minimum, so it can't even fund
   *    both channels' minimums. (At exactly the minimum the split still shows,
   *    locked to each channel's minimum.)
   */
  const minimumExceedsAccountMax =
    budgetExceedsAccountMax ||
    (!noMaxBudget && channelMinimums !== null && channelMinimums.total > accountMaxBudget);
  const budgetBelowSplitMinimum =
    !minimumExceedsAccountMax && channelMinimums !== null && targetValue < channelMinimums.total;
  const splitUnavailable = minimumExceedsAccountMax || budgetBelowSplitMinimum;

  /*
   * "Can't be funded" is a budget-level problem (the minimum exceeds the account
   * maximum), so it lives in the Budget card in place of the recommended-budget
   * info alert — not on the split (which shows an empty state).
   */
  const accountMaxAlert = {
    title: t('This campaign can’t be funded within your account limit'),
    // When the per-channel minimums have loaded we can name the exact total;
    // otherwise (rejected recommendation) we only know it exceeds the max.
    description: channelMinimums
      ? t(
          'The minimum budget required to run both Display and Meta is {{min}}, which exceeds your account’s maximum campaign budget of {{max}}. Shorten the campaign, adjust the targeted audience, or contact your account admin to raise the limit.',
          {
            min: numeral(channelMinimums.total).format('$0,0'),
            max: numeral(accountMaxBudget).format('$0,0'),
          },
        )
      : t(
          'This campaign’s minimum budget exceeds your account’s maximum campaign budget of {{max}}. Shorten the campaign, adjust the targeted audience, or contact your account admin to raise the limit.',
          { max: numeral(accountMaxBudget).format('$0,0') },
        ),
    type: 'danger' as const,
    name: 'alert_account_max_exceeded',
  };

  let alertProps: null | { title: string; description: string; type: TAlertType; name: string } =
    null;
  if (minimumExceedsAccountMax) {
    // Replaces the recommended-budget info alert when the campaign is unfundable.
    alertProps = accountMaxAlert;
  } else if (targetValue < minBudget) {
    alertProps = alertMap['danger'];
  } else if (targetValue < recommendationRaw && !isFacebook) {
    alertProps = alertMap['info'];
  }

  function handleClearDate(type: 'end' | 'start'): () => void {
    return function () {
      campaign.set({ [`date_${type}`]: undefined });
    };
  }

  return (
    <Flex
      className={classNames(
        isOmniCampaign ? wizardStyles.flexRoot : wizardStyles.formRoot,
        styles.root,
      )}
      data-name={'budget_step'}
      justify={isOmniCampaign ? 'center' : 'space-between'}
    >
      <div>
        <Card width={'narrow'}>
          <Card.Header title={'Duration'} />
          <Card.Content addVerticalGap={true}>
            <Flex gap={cssVar('--spacing-4')} justify={'space-between'}>
              <DatePicker
                autoComplete={'off'}
                dateFormat={'MMM d, yyyy h:mm aa'}
                disabled={!isDraft && isStarted}
                isClearable={true}
                isFullWidth={true}
                label={t('Start date')}
                minDate={momentToDate(campaign.getMinStartDate())}
                name={'date_start'}
                onClear={handleClearDate('start')}
                onDateStrChange={handleOnChangeSendStart}
                showTimeSelect={true}
                timeIntervals={5}
                timezone={timezone}
                validationError={validationErrors.date_start}
                value={startTimeStamp}
              />
              <DatePicker
                autoComplete={'off'}
                dateFormat={'MMM d, yyyy h:mm aa'}
                disabled={!isDraft && (isComplete || isDeprecatedMetaCampaign)}
                isClearable={true}
                isFullWidth={true}
                label={t('End date')}
                minDate={startMoment ? momentToDate(startMoment.add(getMinDuration())) : undefined}
                name={'date_end'}
                onClear={handleClearDate('end')}
                onDateStrChange={handleOnChangeDateEnd}
                showTimeSelect={true}
                timeIntervals={5}
                timezone={timezone}
                validationError={validationErrors.date_end}
                value={endTimeStamp}
              />
            </Flex>
          </Card.Content>
        </Card>
        <Card width={'narrow'}>
          <Card.Header description={description} title={'Budget'} />
          <Card.Content addVerticalGap={true}>
            <Flex align={'center'} gap={cssVar('--spacing-4')} justify={'space-between'}>
              {!canEditBudget && (
                <EmptyState
                  description={t('Select a start and end date to access {{target}} selection.', {
                    target: noDatesTarget,
                  })}
                  label={'No dates selected'}
                  name={'budget_empty_dates'}
                  theme={'neutral'}
                  width={'full'}
                />
              )}
              {!isMonetization && canEditBudget && !isFacebook && (
                <RecommendedBudget
                  budget={formattedBudget || minBudgetFormatted}
                  campaign={campaign as TTDCampaign}
                  hasChannelSplit={isOmniCampaign && (campaign as OmniCampaign).isDualChannel}
                  maxBudget={accountMaxBudget}
                  onClick={handleSetRecommendedBudget}
                  recBudget={recBudget}
                  setBudgetExceedsAccountMax={setBudgetExceedsAccountMax}
                  setBudgetRecommendationFailed={setBudgetRecommendationFailed}
                  setIsRecommendationLoading={setIsRecommendationLoading}
                  setMinBudgets={setChannelMinimums}
                  setRecBudget={setRecBudget}
                  setRecommendedAmounts={setRecommendedAmounts}
                  setRecommendedSplit={setRecommendedSplit}
                  targetingsWithData={targetingsWithData}
                />
              )}
              {canEditBudget && (
                <NumberInput
                  additionalContent={targetRecommendation}
                  className={classNames({ [styles.budgetInput]: !isMonetization })}
                  data-name={isMonetization ? 'impressions_target_value' : 'budget_target_value'}
                  disabled={!canEditBudget}
                  helpPlacement={'top'}
                  helpText={overspendText}
                  label={isMonetization ? t('Impressions') : t('Budget')}
                  min={isMonetization ? 10 : minBudget}
                  name={'budget'}
                  onChange={handleTargetValueChange}
                  prefix={isMonetization ? null : '$'}
                  /* The unfundable case is explained by the account-max alert
                   * below; don't duplicate it under the input. */
                  validationError={minimumExceedsAccountMax ? undefined : validationErrors.budget}
                  value={campaign.get('exposure_settings').target_value}
                />
              )}
            </Flex>

            {alertProps && !isMonetization && canEditBudget && (
              <Alert
                className={styles.marginBuster}
                description={alertProps.description}
                name={alertProps.name}
                title={alertProps.title}
                type={alertProps.type}
              />
            )}

            {isMonetization && canEditBudget && (
              <NumberInput
                data-name={'monetization_value'}
                helpText={t(
                  'How much is this campaign worth to you? Usually this is how much your partner paid to you to run the campaign. This value is only used to provide reporting context.',
                )}
                label={t('Sponsor package value')}
                min={0}
                name={'monetization_value'}
                onChange={handleMonetizationValueChange}
                prefix={'$'}
                required={true}
                validationError={validationErrors.monetization_value}
                value={campaign.get('monetization_value')}
              />
            )}
          </Card.Content>
        </Card>
        {isOmniCampaign &&
          (campaign as OmniCampaign).isDualChannel &&
          canEditBudget &&
          (splitUnavailable ? (
            <Card width={'narrow'}>
              <Card.Header
                description={t('Divide your total budget.')}
                title={t('Channel Budget Split')}
              />
              <Card.Content>
                <EmptyState
                  description={t(
                    'Select a valid budget to divide it between the Display and Meta channels.',
                  )}
                  label={t('No valid budget selected')}
                  name={'channel_split_empty'}
                  theme={'neutral'}
                  width={'full'}
                />
              </Card.Content>
            </Card>
          ) : (
            <ChannelBudgetSplit
              audienceData={audienceData}
              campaign={campaign as OmniCampaign}
              channelMinimums={channelMinimums}
              isRecommendationLoading={isRecommendationLoading}
              recommendedAmounts={recommendedAmounts}
              recommendedSplit={recommendedSplit}
            />
          ))}
      </div>

      {isOmniCampaign && !isOmniReachCampaign && (
        <div className={wizardStyles.right}>
          <TargetAudienceOverview
            isSticky={true}
            metaLookalikeLabel={metaLookalikeLabel}
            showDisplay={showDisplay}
            showMeta={showMeta}
            {...audienceData}
          />
        </div>
      )}
    </Flex>
  );
}

export default observer(AdWizardBudgetStep);
